import { create } from 'zustand';
import { api, post } from '@/lib/api';

export interface SessionUser {
  id: string;
  username: string;
  role: 'agent' | 'advertiser';
  agent_id: string | null;
  email: string | null;
  phone: string | null;
  created_at: number;
  last_login_at: number | null;
}

interface SessionState {
  user: SessionUser | null;
  scope: 'full' | 'set-password' | null;
  /** `error`: the server could not be asked. Not the same as signed out, so nothing redirects on it. */
  status: 'idle' | 'loading' | 'ready' | 'error';
  load: () => Promise<void>;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setPassword: (password: string, confirm: string) => Promise<void>;
}

/**
 * Pinia-shaped: state and the actions that change it live together, components select what
 * they need. The server cookie is the real session; this store only mirrors it.
 */
export const useSession = create<SessionState>((set, get) => ({
  user: null,
  scope: null,
  status: 'idle',

  load: async () => {
    set({ status: 'loading' });
    try {
      const data = await api<{ user: SessionUser | null; scope?: 'full' | 'set-password' }>('/auth/me');
      set({ user: data.user, scope: data.scope ?? null, status: 'ready' });
    } catch {
      // Left `loading`, every screen waiting on the session waited for ever.
      set({ status: 'error' });
    }
  },

  login: async (identifier, password) => {
    await post('/auth/login', { identifier, password });
    await get().load();
  },

  logout: async () => {
    await post('/auth/logout');
    set({ user: null, scope: null });
  },

  setPassword: async (password, confirm) => {
    await post('/auth/set-password', { password, confirm });
    await get().load();
  },
}));
